import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
import moment from 'moment';
// utils
import { API_URL } from '../config';
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  list: [],
  attendanceList: [],
  email: '',
  isSubmitting: false,
  successMessage: null,
  errorMessage: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, list, email, isSubmitting, successMessage, errorMessage } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      list,
      email,
      isSubmitting,
      successMessage,
      errorMessage,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  EMPLOYERLIST: (state, action) => {
    const { list } = action.payload;

    return {
      ...state,
      list,
      isSubmitting: false,
      successMessage: null,
      errorMessage: null,
    };
  },
  ATTENDANCESEARCH: (state, action) => {
    const { list } = action.payload;

    return {
      ...state,
      attendanceList: list,
      errorMessage: null,
    };
  },
  SETMAIL: (state, action) => {
    const { email } = action.payload;

    return {
      ...state,
      email,
    };
  },
  SUBMITTING: (state, action) => {
    const { isSubmitting } = action.payload;
    
    return {
      ...state,
      isSubmitting,
    };
  },
  RESET: (state) => ({
    ...state,
    isSubmitting: false,
    successMessage: null,
    errorMessage: null,
  }),
  CHECKIN_OK: (state, action) => {
    const { successMessage } = action.payload;
    return {
      ...state,
      email: '',
      isSubmitting: false,
      successMessage,
      errorMessage: null,
    }
  },
  CHECKIN_ERROR: (state, action) => {
    const { errorMessage } = action.payload;

    return {
      ...state,
      isSubmitting: false,
      successMessage: null,
      errorMessage,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  setEmail: () => Promise.resolve(),
  employerList: () => Promise.resolve(),
  attendanceSearch: () => Promise.resolve(),
  attendanceDownload: () => Promise.resolve(),
  employerChekin: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');

        // if (accessToken && isValidToken(accessToken)) {
        if (accessToken) {
          const member = JSON.parse(localStorage.getItem('user'));
          // const response = await axios.get(`${API_URL}/api/member/${memberId}/info`);
          // const { member } = response.data;
          setSession(accessToken, member);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: member,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/member/login`, {
      email,
      password
    });
    const { token, member } = response.data;
    setSession(token, member);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: member,
      },
    });
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    localStorage.removeItem('accessToken');
    dispatch({ type: 'LOGOUT' });
  };

  const setEmail = async (email) => {
    dispatch({
      type: 'SETMAIL',
      payload: {
        email,
      },
    });
  };

  const employerList = async () => {
    const response = await axios.get(`${API_URL}/api/general/checker`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.accessToken}`
      }
    });

    dispatch({
      type: 'EMPLOYERLIST',
      payload: {
        list: response.data,
      },
    });
  };

  const attendanceSearch = async (startDate, endDate, owner) => {
    const response = await axios.get(`${API_URL}/api/general/checker/attendance`, {
      params: {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        owner
      },
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.accessToken}`
      }
    });

    dispatch({
      type: 'ATTENDANCESEARCH',
      payload: {
        list: response.data,
      },
    });
  };

  const attendanceDownload = async (startDate, endDate) => {
    const response = await axios.get(`${API_URL}/api/general/checker/attendance/download`, {
      params: {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD')
      },
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.accessToken}`
      }
    });

    // dispatch({
    //   type: 'ATTENDANCESEARCH',
    //   payload: {
    //     list: response.data,
    //   },
    // });
  };

  const employerChekin = async (email) => {
    dispatch({
      type: 'SUBMITTING',
      payload: {
        isSubmitting: true,
      },
    });

    const response = await axios.post(`${API_URL}/api/general/checker`, {
      email,
      type: 'form',
      code: Date.now(),
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.accessToken}`
      }
    });

    const { error, message } = response.data;
    if (error) {

      dispatch({
        type: 'CHECKIN_ERROR',
        payload: {
          errorMessage: message,
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      dispatch({
        type: 'RESET',
      });

      return;
    }

    dispatch({
      type: 'CHECKIN_OK',
      payload: {
        successMessage: message,
      },
    });

    employerList();
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        setEmail,
        employerList,
        attendanceSearch,
        attendanceDownload,
        employerChekin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
